import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  HandHeart, Mail, Phone, FileSignature, Briefcase, 
  Palette, Check, Circle, ArrowRight, Loader2, Shield,
  CheckCircle2, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/status`);
      setStatus(response.data);
      
      // If all steps complete, redirect to dashboard
      if (response.data.onboarding_completed) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
      toast.error('Failed to load onboarding status');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const getE164Phone = () => {
    const numbers = phoneNumber.replace(/\D/g, '');
    return `+1${numbers}`;
  };

  const sendVerificationCode = async () => {
    if (phoneNumber.replace(/\D/g, '').length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setSendingCode(true);
    try {
      await axios.post(`${API}/onboarding/sms/send-code`, {
        phone_number: getE164Phone()
      });
      setCodeSent(true);
      toast.success('Verification code sent! Check your phone.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      await axios.post(`${API}/onboarding/sms/verify`, {
        phone_number: getE164Phone(),
        code: verificationCode
      });
      toast.success('Phone number verified successfully!');
      setPhoneDialogOpen(false);
      setCodeSent(false);
      setVerificationCode('');
      fetchOnboardingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const handleGmailConnect = async () => {
    try {
      const response = await axios.get(`${API}/oauth/google/login?origin=${encodeURIComponent(window.location.origin)}`);
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error('Failed to start Google connection. Please try again.');
    }
  };

  const markStepComplete = async (stepName) => {
    try {
      await axios.put(`${API}/onboarding/step/${stepName}`);
      fetchOnboardingStatus();
    } catch (error) {
      console.error('Error marking step complete:', error);
    }
  };

  const steps = [
    {
      id: 'gmail_connected',
      title: 'Connect Your Gmail',
      description: 'Send candidate emails and offer letters from your Gmail account',
      icon: Mail,
      completed: status?.gmail_connected,
      action: handleGmailConnect,
      actionLabel: 'Connect Gmail',
      completedText: status?.gmail_email ? `Connected: ${status.gmail_email}` : 'Connected'
    },
    {
      id: 'sms_verified',
      title: 'Add Your SMS Notification Number',
      description: 'Receive text alerts for new applications and hiring activity',
      icon: Phone,
      completed: status?.sms_verified,
      action: () => setPhoneDialogOpen(true),
      actionLabel: 'Add Phone Number',
      completedText: status?.sms_phone ? `Verified: ${status.sms_phone}` : 'Verified'
    },
    {
      id: 'offer_template_setup',
      title: 'Set Up Your Offer Letter Template',
      description: 'Customize the offer letter sent to new hires',
      icon: FileSignature,
      completed: status?.offer_template_setup,
      action: () => {
        markStepComplete('offer_template_setup');
        navigate('/dashboard/settings?tab=offer-letter');
      },
      actionLabel: 'Customize Template',
      completedText: 'Template configured'
    },
    {
      id: 'first_job_created',
      title: 'Add Your First Job Listing',
      description: 'Post a position to start receiving applications',
      icon: Briefcase,
      completed: status?.first_job_created,
      action: () => navigate('/dashboard/job-listings'),
      actionLabel: 'Create Job Listing',
      completedText: 'Job listing created'
    },
    {
      id: 'careers_page_customized',
      title: 'Customize Your Careers Page',
      description: 'Add your personal touch to the public careers page',
      icon: Palette,
      completed: status?.careers_page_customized,
      action: () => {
        markStepComplete('careers_page_customized');
        navigate('/dashboard/settings?tab=general');
      },
      actionLabel: 'Customize Page',
      completedText: 'Page customized'
    }
  ];

  const completedCount = steps.filter(s => s.completed).length;
  const progressPercent = (completedCount / steps.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#4B2D6E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className="bg-[#4B2D6E] text-white py-6 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <HandHeart className="h-10 w-10 text-[#E8722A]" />
            <div>
              <h1 className="text-xl md:text-2xl font-bold font-[Lato] text-[#E8722A]">SeniorPeerConnect™</h1>
              <p className="text-sm text-gray-200">Setup Wizard</p>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Welcome, {user?.name?.split(' ')[0] || 'there'}! 👋</h2>
            <p className="text-gray-200">Let's get your SeniorPeerConnect™ account set up. This will only take a few minutes.</p>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#4B2D6E]">Setup Progress</span>
            <span className="text-sm text-[#666666]">{completedCount} of {steps.length} complete</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#4B2D6E] to-[#E8722A] transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Security Note */}
        <Card className="mb-8 border-l-4 border-[#4B2D6E] bg-[#4B2D6E]/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#4B2D6E] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-[#333333]">
                  <strong className="text-[#4B2D6E]">Your security matters.</strong> SeniorPeerConnect™ uses industry-standard OAuth for Gmail — we never see or store your password. Your data is encrypted and secure.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Checklist */}
        <div className="space-y-4">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <Card 
                key={step.id}
                className={`transition-all duration-300 ${
                  step.completed 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-white hover:shadow-md'
                }`}
                data-testid={`onboarding-step-${step.id}`}
              >
                <CardContent className="py-5">
                  <div className="flex items-center gap-4">
                    {/* Step Number / Check */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      step.completed 
                        ? 'bg-green-500 text-white' 
                        : 'bg-[#4B2D6E]/10 text-[#4B2D6E]'
                    }`}>
                      {step.completed ? (
                        <Check className="h-6 w-6" />
                      ) : (
                        <Icon className="h-6 w-6" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold text-lg ${
                        step.completed ? 'text-green-700' : 'text-[#333333]'
                      }`}>
                        {step.title}
                      </h3>
                      <p className={`text-sm ${
                        step.completed ? 'text-green-600' : 'text-[#666666]'
                      }`}>
                        {step.completed ? step.completedText : step.description}
                      </p>
                    </div>

                    {/* Action Button */}
                    {!step.completed && (
                      <Button
                        onClick={step.action}
                        className="bg-[#E8722A] hover:bg-[#D6621F] text-white flex-shrink-0"
                        data-testid={`onboarding-action-${step.id}`}
                      >
                        {step.actionLabel}
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    )}

                    {step.completed && (
                      <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Skip for Now */}
        <div className="mt-8 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#666666] hover:text-[#4B2D6E]"
            data-testid="skip-onboarding-btn"
          >
            Skip for now — I'll finish later
          </Button>
          <p className="text-xs text-[#999999] mt-2">
            You can always complete setup from your dashboard
          </p>
        </div>
      </main>

      {/* Phone Verification Dialog */}
      <Dialog open={phoneDialogOpen} onOpenChange={setPhoneDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#4B2D6E]">
              <Phone className="h-5 w-5 text-[#E8722A]" />
              Add Your SMS Notification Number
            </DialogTitle>
            <DialogDescription>
              Enter the phone number where you'd like to receive text notifications for new candidate applications, interview reminders, and hiring activity.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!codeSent ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    className="text-lg tracking-wide"
                    maxLength={14}
                    data-testid="phone-input"
                  />
                  <p className="text-xs text-[#666666]">US phone numbers only</p>
                </div>
                <Button
                  onClick={sendVerificationCode}
                  disabled={sendingCode || phoneNumber.replace(/\D/g, '').length !== 10}
                  className="w-full bg-[#E8722A] hover:bg-[#D6621F]"
                  data-testid="send-code-btn"
                >
                  {sendingCode ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>
              </>
            ) : (
              <>
                <div className="text-center mb-4">
                  <p className="text-sm text-[#666666]">
                    We sent a 6-digit code to <strong>{phoneNumber}</strong>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-2xl tracking-[0.5em] text-center font-mono"
                    maxLength={6}
                    data-testid="verification-code-input"
                  />
                </div>
                <Button
                  onClick={verifyCode}
                  disabled={verifying || verificationCode.length !== 6}
                  className="w-full bg-[#4B2D6E] hover:bg-[#3a2357]"
                  data-testid="verify-code-btn"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Save'
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCodeSent(false);
                    setVerificationCode('');
                  }}
                  className="w-full text-[#666666]"
                >
                  Use a different number
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingPage;
